import { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultMode?: 'login' | 'signup';
}

export default function AuthModal({ isOpen, onClose, defaultMode = 'login' }: AuthModalProps) {
  const [mode, setMode] = useState<'login' | 'signup' | 'reset' | 'find'>(defaultMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { signIn, signUp, resetPassword, findAccount } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (mode === 'login') {
        await signIn(email, password);
        setSuccess('로그인되었습니다!');
        setTimeout(() => {
          onClose();
          resetForm();
        }, 1000);
      } else if (mode === 'signup') {
        // 회원가입 시 이메일 형식 검증
        if (!email || !email.includes('@') || !email.includes('.')) {
          setError('올바른 이메일 형식을 입력해주세요.\n예: example@email.com');
          setLoading(false);
          return;
        }

        if (!password || password.length < 6) {
          setError('비밀번호는 최소 6자 이상이어야 합니다.');
          setLoading(false);
          return;
        }

        if (!name || name.trim().length === 0) {
          setError('이름을 입력해주세요.');
          setLoading(false);
          return;
        }

        if (!phone || phone.trim().length === 0) {
          setError('연락처를 입력해주세요.');
          setLoading(false);
          return;
        }

        await signUp(email, password, name, phone);
        setSuccess('회원가입이 완료되었습니다! 바로 로그인하실 수 있습니다.');
        setTimeout(() => {
          setMode('login');
          setSuccess('');
        }, 2000);
      } else if (mode === 'reset') {
        try {
          await resetPassword(email);
          setSuccess('비밀번호 재설정 이메일이 발송되었습니다. 이메일을 확인해주세요.');
          setTimeout(() => {
            setMode('login');
            setSuccess('');
          }, 3000);
        } catch (resetError: any) {
          if (resetError.message.includes('fetch') || resetError.message.includes('network')) {
            setError('네트워크 연결을 확인해주세요.\n\n가능한 원인:\n• 인터넷 연결 문제\n• Supabase 서비스 접속 불가\n• 이메일 발송 설정 미완료\n\n관리자에게 문의해주세요.');
          } else {
            throw resetError;
          }
        }
      } else if (mode === 'find') {
        const result = await findAccount(name, phone);
        if (result) {
          setSuccess(`가입된 이메일을 찾았습니다:\n\n${result}`);
        } else {
          setError('입력하신 정보로 가입된 계정을 찾을 수 없습니다.\n\n• 이름과 연락처를 정확히 입력했는지 확인해주세요\n• 다른 정보로 가입하셨을 수 있습니다');
        }
      }
    } catch (err: any) {
      console.error('인증 오류:', err);
      
      // Supabase 오류 메시지를 한국어로 변환
      let errorMessage = err.message || '오류가 발생했습니다.';
      
      if (errorMessage.includes('Unable to validate email address: invalid format')) {
        errorMessage = '올바른 이메일 형식을 입력해주세요.\n예: example@email.com';
      } else if (errorMessage.includes('Invalid login credentials')) {
        errorMessage = '이메일 또는 비밀번호가 올바르지 않습니다.';
      } else if (errorMessage.includes('Email not confirmed')) {
        errorMessage = '이메일 인증이 완료되지 않았습니다.';
      } else if (errorMessage.includes('User already registered')) {
        errorMessage = '이미 가입된 이메일입니다.';
      } else if (errorMessage.includes('Password should be at least 6 characters')) {
        errorMessage = '비밀번호는 최소 6자 이상이어야 합니다.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setName('');
    setPhone('');
    setError('');
    setSuccess('');
  };

  const handleClose = () => {
    onClose();
    resetForm();
    setMode(defaultMode);
  };

  const handleModeChange = (newMode: 'login' | 'signup' | 'reset' | 'find') => {
    setMode(newMode);
    setError('');
    setSuccess('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-8">
          {/* 헤더 */}
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-bold text-gray-800">
              {mode === 'login' && '로그인'}
              {mode === 'signup' && '회원가입'}
              {mode === 'reset' && '비밀번호 재설정'}
              {mode === 'find' && '계정 찾기'}
            </h2>
            <button
              onClick={handleClose}
              className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center text-gray-600 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          {/* 폼 */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {mode === 'signup' && (
              <>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    이메일 *
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                    placeholder="example@email.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    비밀번호 *
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                    placeholder="비밀번호 (최소 6자)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    이름 *
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                    placeholder="이름을 입력해주세요"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    연락처 *
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                    placeholder="010-0000-0000"
                  />
                </div>
              </>
            )}

            {mode === 'find' && (
              <>
                <div className="p-4 bg-blue-50 rounded-xl mb-4">
                  <p className="text-sm text-blue-800">
                    회원가입 시 입력하신 이름과 연락처를 입력하시면 가입된 이메일을 확인하실 수 있습니다.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    이름 *
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                    placeholder="가입 시 입력한 이름"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    연락처 *
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                    placeholder="가입 시 입력한 연락처"
                  />
                </div>
              </>
            )}

            {(mode === 'login' || mode === 'reset') && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  이메일 *
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                  placeholder="이메일을 입력해주세요"
                />
              </div>
            )}

            {mode === 'login' && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  비밀번호 *
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                  placeholder="비밀번호를 입력해주세요 (최소 6자)"
                />
              </div>
            )}

            {/* 에러/성공 메시지 */}
            {error && (
              <div className="p-4 bg-red-100 text-red-700 rounded-xl text-sm whitespace-pre-line">
                {error}
              </div>
            )}

            {success && (
              <div className="p-4 bg-green-100 text-green-700 rounded-xl text-sm whitespace-pre-line">
                {success}
              </div>
            )}

            {/* 제출 버튼 */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-green-500 hover:bg-green-600 disabled:bg-gray-300 text-white py-4 rounded-xl text-lg font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  처리 중...
                </>
              ) : (
                <>
                  {mode === 'login' && '로그인'}
                  {mode === 'signup' && '회원가입'}
                  {mode === 'reset' && '재설정 이메일 발송'}
                  {mode === 'find' && '계정 찾기'}
                </>
              )}
            </button>
          </form>

          {/* 모드 전환 */}
          <div className="mt-6 text-center space-y-2">
            {mode === 'login' && (
              <>
                <p className="text-gray-600 text-sm">
                  계정이 없으신가요?
                  <button
                    type="button"
                    onClick={() => handleModeChange('signup')}
                    className="ml-2 text-green-600 font-semibold hover:text-green-700 cursor-pointer"
                  >
                    회원가입
                  </button>
                </p>
                <div className="flex items-center justify-center gap-4 text-sm">
                  <button
                    type="button"
                    onClick={() => handleModeChange('find')}
                    className="text-gray-600 hover:text-gray-800 cursor-pointer"
                  >
                    계정 찾기
                  </button>
                  <span className="text-gray-400">|</span>
                  <button
                    type="button"
                    onClick={() => handleModeChange('reset')}
                    className="text-gray-600 hover:text-gray-800 cursor-pointer"
                  >
                    비밀번호 재설정
                  </button>
                </div>
              </>
            )}

            {mode === 'signup' && (
              <p className="text-gray-600 text-sm">
                이미 계정이 있으신가요?
                <button
                  type="button"
                  onClick={() => handleModeChange('login')}
                  className="ml-2 text-green-600 font-semibold hover:text-green-700 cursor-pointer"
                >
                  로그인
                </button>
              </p>
            )}

            {(mode === 'reset' || mode === 'find') && (
              <p className="text-gray-600 text-sm">
                로그인 화면으로 돌아가기
                <button
                  type="button"
                  onClick={() => handleModeChange('login')}
                  className="ml-2 text-green-600 font-semibold hover:text-green-700 cursor-pointer"
                >
                  로그인
                </button>
              </p>
            )}
          </div>

          {/* 디버깅 도움말 */}
          {mode === 'signup' && (
            <div className="mt-6 p-4 bg-blue-50 rounded-xl">
              <h4 className="text-sm font-semibold text-blue-800 mb-2">회원가입 안내</h4>
              <ul className="text-xs text-blue-700 space-y-1">
                <li>• 이메일 형식: example@email.com</li>
                <li>• 비밀번호는 최소 6자 이상 입력해주세요</li>
                <li>• 모든 항목은 필수 입력 사항입니다</li>
              </ul>
            </div>
          )}

          {mode === 'login' && (
            <div className="mt-6 p-4 bg-blue-50 rounded-xl">
              <h4 className="text-sm font-semibold text-blue-800 mb-2">로그인이 안 되시나요?</h4>
              <ul className="text-xs text-blue-700 space-y-1">
                <li>• 이메일 주소와 비밀번호를 정확히 입력했는지 확인해주세요</li>
                <li>• 회원가입 시 사용한 이메일과 동일한지 확인해주세요</li>
                <li>• 비밀번호는 대소문자를 구분합니다</li>
                <li>• 이메일이 기억나지 않으면 '계정 찾기'를 이용해주세요</li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
